<template>
  <div class="keyword-navigator">
    <v-overlay :value="keywordSwitching" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>
    <div v-if="error" class="keyword-navigator__no-document-keywords">
      <BaseError :error="error" />
    </div>
    <template v-else>
      <div v-if="hasScheduledKeywordIndexing" class="keyword-navigator__loading">
        <OpportunityDocumentsQueueState />
      </div>
      <div v-else-if="documentKwPending" class="keyword-navigator__loading">
        <BaseCircularLoadingBar :size="32" />
      </div>
      <div v-else-if="!keywords.length" class="keyword-navigator__no-document-keywords">
        {{ $t('page.analysis.keyword-navigator.no-keyword') }}
        <BaseButton @click.native="toggleBeacon" is-small>{{ $t('page.keywords.contact') }}</BaseButton>
      </div>
      <div v-else class="keyword-navigator__categories">
        <KeywordSearchBar
          class="keyword-navigator__search-bar"
          name="search"
          type="text"
          :placeholder="$t('page.analysis.keyword-navigator.search-keyword')"
          hasGrayBackground
        ></KeywordSearchBar>
        <div v-if="pdfEnginePending" class="keyword-navigator__loading">
          <BaseCircularLoadingBar :size="32" />
        </div>
        <template v-else>
          <div class="keyword-navigator__edit-keywords">
            <div class="keyword-navigator__edit-keywords-actions">
              <DocumentsWithoutKeywordsWarning @document-click="goToDocument" />
              <BaseButton has-icon-left is-small isSquare isGrey @click.native="opensDocKeywordsSettings">
                <BaseIcon name="edit-2" />
                {{ $t('page.admin-document-keywords.edit-document-keywords') }}
              </BaseButton>
            </div>
          </div>
          <div class="keyword-navigator__category" v-for="(category, index) in matchInformation" :key="index">
            <div class="keyword-navigator__category--item" v-if="category">
              <div class="keyword-navigator__kw-category--title">
                <div class="keyword-navigator__kw-category-color" :style="{ background: category.colorCode }"></div>
                <div class="keyword-navigator__kw-category-name">{{ category.name }}</div>
                <div class="keyword-navigator__kw-category-occurrence">{{ categoryLength(category) }}</div>
              </div>
              <div class="keyword-navigator__category--content">
                <BaseCircularLoadingBar
                  v-if="pdfEnginePending"
                  :size="16"
                  :text="$t('page.analysis.keyword-navigator.loading-keywords')"
                />
                <div v-if="!category.results.length" class="keyword-navigator__no-items">
                  {{ $t('page.analysis.keyword-navigator.no-keyword-found') }}
                </div>
                <div v-else>
                  <div
                    v-for="(result, index) in sortedCategoryResults(category.results)"
                    :key="index"
                    class="keyword-navigator__kw-category-items"
                    :class="{ 'is-selected': isKeywordSelected(result.keyword) }"
                    @click.stop="toggleKeyword(result)"
                  >
                    <div class="keyword-navigator__kw-category-items--description">
                      <span
                        class="keyword-navigator__kw-category-items--name"
                        v-tooltip="{
                          content: result.keyword.value,
                          delay: 400,
                        }"
                        >{{ result.keyword.value }}</span
                      >
                      <span
                        v-if="isKeywordSelected(result.keyword)"
                        class="keyword-navigator__kw-category-items--length"
                        >{{ displayedKeywordNavigation(result) }}</span
                      >
                      <span v-else class="keyword-navigator__kw-category-items--length" ref="keywordCount">
                        {{ result.occurrences }}</span
                      >
                    </div>

                    <div
                      class="keyword-navigator__kw-category-items--navigation"
                      v-if="isKeywordSelected(result.keyword)"
                    >
                      <BaseRoundButton
                        v-show="keywordOccurrences > 1"
                        @click.native.stop="goToPrevious"
                        icon="chevron-left"
                        :disabled="keywordIndex === 1"
                        is-transparent
                      />
                      <BaseRoundButton
                        v-show="keywordOccurrences > 1"
                        @click.native.stop="goToNext"
                        icon="chevron-right"
                        :disabled="keywordIndex === keywordOccurrences"
                        is-transparent
                      />
                      <div
                        v-if="isKeywordSelected(result.keyword)"
                        class="keyword-navigator__kw-category-item--button"
                        @click.stop="searchDone()"
                      >
                        {{ $t('page.analysis.keyword-navigator.done') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <DocKeywordsSettingsDialog ref="keywordSettings" />
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseError from '@/components/base/BaseError.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import DocKeywordsSettingsDialog from '@/components/page-document-keywords/DocKeywordsSettingsDialog.vue';
import DocumentsWithoutKeywordsWarning from '@/components/page-opportunity-details/DocumentsWithoutKeywordsWarning.vue';
import KeywordNavigationMixin from '@/mixins/keyword-navigation-mixin';
import KeywordSearchBar from '@/components/pdf-viewer/KeywordSearchBar';

import { Beacon } from '@/plugins/beacon';
import { bus } from '@/main';
import { mapActions, mapGetters } from 'vuex';
import OpportunityDocumentsQueueState from '@/components/page-opportunity-details/OpportunityDocumentsQueueState.vue';

export default {
  name: 'KeywordNavigation',
  components: {
    OpportunityDocumentsQueueState,
    KeywordSearchBar,
    BaseRoundButton,
    BaseCircularLoadingBar,
    BaseButton,
    BaseIcon,
    BaseError,
    DocKeywordsSettingsDialog,
    DocumentsWithoutKeywordsWarning,
  },
  mixins: [KeywordNavigationMixin],
  computed: {
    ...mapGetters('opportunityDocumentsPdfEngine', ['matchInformation', 'matchedKwByDocument', 'error']),
    ...mapGetters('opportunityDocumentsPdfEngine', { pdfEnginePending: 'pending' }),
    ...mapGetters('documentKeywords', ['categories', 'keywords']),
    ...mapGetters('documentKeywords', { documentKwPending: 'pending' }),
    ...mapGetters('opportunityDocuments', ['opportunityDocuments', 'opportunityDocument']),
    ...mapGetters('opportunityKeywordSearch', [
      'selectedKeyword',
      'keywordLocations',
      'keywordIndex',
      'keywordOccurrences',
      'keywordSwitching',
    ]),
    ...mapGetters('opportunityDocumentsQueue', ['hasScheduledKeywordIndexing']),
  },
  methods: {
    ...mapActions('opportunityKeywordSearch', [
      'setSelectedKeyword',
      'setKeywordSwitching',
      'findKeywordLocations',
      'resetSelectedKeyword',
    ]),
    ...mapActions('opportunityKeywordSearch', ['setSelectedKeyword', 'findKeywordLocations', 'resetSelectedKeyword']),
    opensDocKeywordsSettings() {
      this.$refs.keywordSettings.openDialog();
    },
    goToDocument(document) {
      if (this.opportunityDocument?.id !== document.id) {
        this.switchDocument(document.id);
      }
    },
    sortedCategoryResults(results) {
      return [...results].sort((a, b) => {
        if (a.keyword.value < b.keyword.value) return -1;
        if (a.keyword.value > b.keyword.value) return 1;
        return 0;
      });
    },
    categoryLength(category) {
      return category.results.length > 0 ? '(' + category.results.length + ')' : '';
    },
    isKeywordSelected(keyword) {
      return this.selectedKeyword ? this.selectedKeyword.id === keyword.id : false;
    },
    searchDone() {
      this.resetHighlightKeywords();
      this.resetSelectedKeyword();
      this.setKeywordSwitching(false);
    },
    toggleBeacon() {
      this.$track.contactUs('keywords');
      Beacon.toggle();
      Beacon.navigate('ask');
    },
    async toggleKeyword(result) {
      if (this.selectedKeyword !== null) this.lowlightKeywords(this.selectedKeyword);
      this.setSelectedKeyword({ keyword: result.keyword, occurrences: result.occurrences });
      this.findKeywordLocations({
        keyword: result.keyword,
        matchedKwByDocument: this.matchedKwByDocument,
        documents: this.opportunityDocuments,
      });

      this.firstOccurrence({ keyword: this.selectedKeyword, locations: this.keywordLocations });
    },
    goToNext() {
      this.nextOccurrence({ keyword: this.selectedKeyword, locations: this.keywordLocations });
    },
    goToPrevious() {
      this.previousOccurrence({ keyword: this.selectedKeyword, locations: this.keywordLocations });
    },
    displayedKeywordNavigation(result) {
      return `${this.keywordIndex} de ${result.occurrences}`;
    },
    // From the occurrence bar over the document (KeywordOccurrenceBar), this panel being out of sight.
    onOccurrenceRequest(action) {
      if (action === 'next') this.goToNext();
      else if (action === 'previous') this.goToPrevious();
      else this.searchDone();
    },
    changeOccurence(event) {
      const labelTagMenu = document.querySelector('.label-tag-menu');
      if (labelTagMenu) return;

      if ((event.key === 'Enter' || event.keyCode === 13) && this.selectedKeyword && this.selectedKeyword.id !== null) {
        this.goToNext();
      }
    },
  },
  created() {
    document.addEventListener('keydown', this.changeOccurence);
    bus.$on('keyword-occurrence', this.onOccurrenceRequest);
  },
  destroyed() {
    this.searchDone();
    document.removeEventListener('keydown', this.changeOccurence);
    bus.$off('keyword-occurrence', this.onOccurrenceRequest);
  },
};
</script>

<style lang="scss" scoped>
.keyword-navigator {
  padding-right: 8px;
  height: 100%;

  &__edit-keywords {
    width: 100%;
    margin-bottom: 4px;
    display: flex;
    justify-content: flex-end;
    font-weight: normal !important;

    &-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  &__search-bar {
    margin-bottom: 8px;
  }

  &__loading {
    height: 100%;
  }

  &__categories {
    height: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    &::-webkit-scrollbar {
      height: 0px;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-large;
      background-color: $color-grey-pale;
    }
  }

  &__category {
    padding: 8px;
    margin-bottom: 8px;
    background-color: $color-neutral-pale;
    border-radius: $border-radius-root;

    &--content {
      padding-left: 12px;
    }
  }

  &__kw-category {
    &--title {
      font-weight: bold;
      font-size: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &-color {
      height: 12px;
      width: 12px;
      border-radius: $border-radius-round;
    }
  }

  &__no-document-keywords {
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: center;
    min-height: 100%;
    text-align: center;
    color: $color-neutral;
  }

  &__kw-category-items {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 28px;
    cursor: pointer;
    border-radius: $border-radius-medium;
    width: 100%;
    padding: 4px 8px;
    margin: 2px 0;
    font-size: 14px;
    white-space: nowrap;

    &--description {
      display: flex;
      max-width: 280px;
    }

    &.is-selected {
      background: $color-hover;
      .keyword-navigator__kw-category-items--name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &--length {
      font-weight: 300;
      color: $color-bluey-gray;
      margin-left: 8px;
      white-space: nowrap;
    }

    &--navigation {
      display: flex;
      align-items: center;
    }

    &--button {
      cursor: pointer;
      font-size: 14px;
      transition: all 200ms ease;
      &:hover {
        color: $color-accent;
      }
    }

    &:hover {
      background: $color-hover;
    }
  }

  &__no-items {
    margin-top: 8px;
    padding: 0 8px;
    color: $color-neutral;
    font-size: 14px;
  }
}
</style>
